import pick from "lodash/pick";
import { TInvitedHA } from "models/healthAuthorities.models";
import {
  IProjectConfiguration,
  ProjectConfiguration,
} from "models/projectConfigurations.models";
import { IProject, ISharedWorkspaceMetadata } from "models/projects.models";
import {
  InvitationStatus,
  PENDING_INVITATION_STATUSES,
} from "utils/constants/healthAuthorities.constants";
import { ProjectTemplateType } from "utils/constants/project.constants";

export const checkHasRelianceTemplate = (
  project: IProject | null | undefined,
) => {
  return project?.templateType === ProjectTemplateType.Reliance;
};

export const checkHasRegulatoryReviewTemplate = (
  project: IProject | null | undefined,
) => {
  return project?.templateType === ProjectTemplateType.RegulatoryReview;
};

export const getConfigurationValue = (
  configurationName: ProjectConfiguration,
  configurationObject: IProjectConfiguration | undefined,
) => configurationObject?.[configurationName];

export const getSupportedProjectMetadata = (
  metadata: ISharedWorkspaceMetadata,
): ISharedWorkspaceMetadata =>
  // GraphQL derives invitedParticipants and shouldUseDeprecatedInvitations for the UI.
  // Asset Service does not accept them in project update metadata.
  pick(metadata, [
    "description",
    "entityId",
    "entityName",
    "eventTypes",
    "eventSubtypes",
    "regulatoryEvent",
    "referenceHA",
    "milestones",
    "relianceParticipants",
  ]);

type TProjectWithInvitedParticipants = {
  metadata?: {
    invitedParticipants?: TInvitedHA[];
  };
};

export const getInvitationCounts = (
  project: TProjectWithInvitedParticipants | null | undefined,
) => {
  const invited = project?.metadata?.invitedParticipants ?? [];
  const invitedTenantIds = new Set(invited.map(({ tenantId }) => tenantId));

  const getTenantIdsByStatus = (
    predicate: (responseStatus: InvitationStatus | null) => boolean,
  ) =>
    new Set(
      invited
        .filter(({ responseStatus }) => predicate(responseStatus))
        .map(({ tenantId }) => tenantId),
    );

  const acceptedTenantIds = getTenantIdsByStatus(
    (responseStatus) => responseStatus === InvitationStatus.Accepted,
  );

  const pendingAcceptanceTenantIds = getTenantIdsByStatus(
    (responseStatus) =>
      !!responseStatus && PENDING_INVITATION_STATUSES.includes(responseStatus),
  );

  const declinedTenantIds = getTenantIdsByStatus(
    (responseStatus) => responseStatus === InvitationStatus.Declined,
  );

  return {
    totalInvited: invitedTenantIds.size,
    accepted: acceptedTenantIds.size,
    pendingAcceptance: pendingAcceptanceTenantIds.size,
    declined: declinedTenantIds.size,
  };
};
